import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { Linking, Pressable, StyleSheet, Text, View } from "react-native";

import { Button, ErrorState, Field, LoadingState, Panel } from "../../../src/components/ui";
import { getCentralFamilyIntakeErrorMessage, getPublicCentralFamilyIntake, CentralFamilyIntakeCase } from "../../../src/lib/central-family-intake";
import { submitPublicCentralFamilyIntakeContact } from "../../../src/lib/central-public-crm";
import { PageShell } from "../../../src/components/layout";
import { central, spacing } from "../../../src/theme/tokens";

export default function FamilyIntakeLandingScreen() {
  const { token } = useLocalSearchParams<{ token?: string | string[] }>();
  const value = Array.isArray(token) ? token[0] : token;
  const [caseRecord, setCaseRecord] = useState<CentralFamilyIntakeCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactConsent, setContactConsent] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const funeralHomeName = publicFuneralHomeName(caseRecord?.funeralHomeName);

  useEffect(() => {
    if (!value) {
      setError("This family link is incomplete.");
      setLoading(false);
      return;
    }
    void getPublicCentralFamilyIntake(value)
      .then((result) => setCaseRecord(result.case))
      .catch((caught) => setError(getCentralFamilyIntakeErrorMessage(caught)))
      .finally(() => setLoading(false));
  }, [value]);

  const saveContactDetails = async () => {
    if (!value || !contactName.trim() || (!contactEmail.trim() && !contactPhone.trim())) return;
    setContactBusy(true);
    setContactMessage("");
    try {
      await submitPublicCentralFamilyIntakeContact({
        consent: contactConsent ? "service_updates" : undefined,
        email: contactEmail,
        eventKey: `intake_${Date.now()}`,
        name: contactName,
        phone: contactPhone,
        token: value,
      });
      setContactMessage(`Your contact details were shared with ${funeralHomeName}.`);
    } catch (caught) {
      setContactMessage(getCentralFamilyIntakeErrorMessage(caught));
    } finally {
      setContactBusy(false);
    }
  };

  return <PageShell role="family">
    <View style={styles.page}>
      {loading ? <LoadingState label="Opening Your Secure Link" description="Preparing your private arrangement workspace." /> : null}
      {!loading && error ? <ErrorState title="Family Link Unavailable" description={error} /> : null}
      {!loading && !error && caseRecord ? <Panel style={styles.panel}>
        <Text style={styles.eyebrow}>PRIVATE FAMILY INTAKE</Text>
        <Text accessibilityRole="header" style={styles.title}>Continue Arrangements For {caseRecord.lovedOneName}</Text>
        <Text style={styles.body}>This private link returns you to the information {funeralHomeName} is preparing for your family. Nothing is final until the care team reviews it with you.</Text>
        <View style={styles.meta}><Text style={styles.metaLabel}>Current Step</Text><Text style={styles.metaValue}>{titleCase(caseRecord.status)}</Text></View>
        <View style={styles.contactSection}>
          <Text accessibilityRole="header" style={styles.sectionTitle}>Family Contact Details</Text>
          <Text style={styles.muted}>Share a reliable way for the care team to follow up about this case.</Text>
          <Field label="Your Name" onChangeText={setContactName} required value={contactName} />
          <Field keyboardType="email-address" label="Email" onChangeText={setContactEmail} value={contactEmail} />
          <Field keyboardType="phone-pad" label="Phone" onChangeText={setContactPhone} value={contactPhone} />
          <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: contactConsent }} onPress={() => setContactConsent((current) => !current)} style={styles.consentRow}>
            <Text style={styles.checkbox}>{contactConsent ? "✓" : ""}</Text>
            <Text style={styles.muted}>I agree to receive service updates about this case.</Text>
          </Pressable>
          {contactMessage ? <Text accessibilityLiveRegion="polite" style={styles.muted}>{contactMessage}</Text> : null}
          <Button disabled={!contactName.trim() || (!contactEmail.trim() && !contactPhone.trim())} label="Save Contact Details" loading={contactBusy} onPress={() => void saveContactDetails()} shape="rounded" variant="secondary" />
        </View>
        <Button label="Open Arrangement Planner" shape="rounded" onPress={() => void Linking.openURL(`https://arrangementstudio.web.app/resume/${encodeURIComponent(value ?? "")}`)} />
      </Panel> : null}
    </View>
  </PageShell>;
}

function titleCase(value: string) { return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase()); }

function publicFuneralHomeName(value: unknown) {
  if (typeof value !== "string" || /[\r\n<>\u0000-\u001F\u007F]/.test(value)) return "your funeral home";
  const name = value.trim().replace(/\s+/g, " ");
  return name.length >= 2 && name.length <= 120 ? name : "your funeral home";
}

const styles = StyleSheet.create({
  body: { color: central.text.secondary, fontFamily: "Manrope_400Regular", fontSize: 16, lineHeight: 25 },
  checkbox: { borderColor: central.border.strong, borderRadius: 4, borderWidth: 1, color: central.action.primary, fontFamily: "Manrope_700Bold", fontSize: 14, height: 22, lineHeight: 20, textAlign: "center", width: 22 },
  consentRow: { alignItems: "flex-start", flexDirection: "row", gap: spacing.sm, minHeight: 44 },
  contactSection: { borderTopColor: central.border.subtle, borderTopWidth: 1, gap: spacing.sm, paddingTop: spacing.md },
  eyebrow: { color: central.text.muted, fontFamily: "Manrope_700Bold", fontSize: 11, letterSpacing: 1 },
  meta: { backgroundColor: central.surface.muted, borderRadius: central.radius.control, gap: spacing.xs, padding: spacing.md },
  metaLabel: { color: central.text.muted, fontFamily: "Manrope_600SemiBold", fontSize: 12 },
  metaValue: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 15 },
  muted: { color: central.text.muted, fontFamily: "Manrope_400Regular", fontSize: 12, lineHeight: 18 },
  page: { alignItems: "center", gap: spacing.md, padding: spacing.lg },
  panel: { gap: spacing.md, maxWidth: 640, width: "100%" },
  sectionTitle: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 16 },
  title: { color: central.text.primary, fontFamily: "CormorantGaramond_700Bold", fontSize: 40, lineHeight: 45 },
});
